import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react'
import ReactDOM from 'react-dom'
import axios from 'axios'
import { useTranslation } from 'react-i18next'
import { stayDayOptions } from '../../search-bar/utils'
import { DatePicker } from '../../date-picker'
import { DateSelectionField } from '../../date-selection/DateSelectionField'
import { DataType } from '../../date-picker/types'
import { IconPlus, IconMinus } from './Icon'
import { DataSearchType, KeyDataSearch, DATA_SEARCH_INIT, PreschoolerBirthdays } from './type'
import { usePlanStore } from '../../../store/planStore'

type Props = {
  initDataSearch: DataSearchType
  onChangeDataSearch: (data: DataSearchType, checkInDates: DataType[]) => void
  isShowCategoryNameOptions?: boolean
}

const isMobileScreen = window.innerWidth < 768
const isTabletScreen = window.innerWidth < 1024 && window.innerWidth > 780
const isSmallMobileScreen = window.innerWidth <= 360

const YearPagination = ({ currentYear, availableYears, setCurrentYear }) => {
  return (
    <div className="flex items-center justify-center">
      <button
        className={`px-3 py-1 text-gray-600 hover:text-black ${
          availableYears.indexOf(currentYear) <= 0 ? 'cursor-not-allowed invisible' : 'hover:cursor-pointer'
        }`}
        onClick={() => {
          const currentIndex = availableYears.indexOf(currentYear)
          if (currentIndex > 0) {
            setCurrentYear(availableYears[currentIndex - 1])
          }
        }}
        disabled={availableYears.indexOf(currentYear) <= 0}
        aria-label="Previous year"
      >
        <span>&#10094;</span>
      </button>
      <div className="mx-4 text-lg">{currentYear}年度</div>
      <button
        className={`px-3 py-1 text-gray-600 hover:text-black ${
          availableYears.indexOf(currentYear) >= availableYears.length - 1
            ? 'cursor-not-allowed invisible'
            : 'hover:cursor-pointer'
        }`}
        onClick={() => {
          const currentIndex = availableYears.indexOf(currentYear)
          if (currentIndex < availableYears.length - 1) {
            setCurrentYear(availableYears[currentIndex + 1])
          }
        }}
        disabled={availableYears.indexOf(currentYear) >= availableYears.length - 1}
        aria-label="Next year"
      >
        <span>&#10095;</span>
      </button>
    </div>
  )
}

export const ModalFilterPlanByCalendar: React.FC<Props> = ({ initDataSearch, onChangeDataSearch }) => {
  const [dataSearch, setDataSearch] = useState<DataSearchType>({ ...initDataSearch })
  const [modalSearchHeight, setModalSearchHeight] = useState(0)
  const [checkInDates, setCheckInDates] = useState<DataType[]>([])
  const [modalSearchContentHeight, setModalSearchContentHeight] = useState(0)
  const [clickedReset, setClickedReset] = useState(false)
  const [currentYear, setCurrentYear] = useState<number>(new Date().getFullYear())

  // Extract available years from checkInDates
  const availableYears = useMemo(() => {
    if (!checkInDates || checkInDates.length === 0) return [new Date().getFullYear()]

    const years = checkInDates.map((item) => {
      const date = new Date(item.date)
      return date.getMonth() < 3 ? date.getFullYear() - 1 : date.getFullYear()
    })
    return [...new Set(years)].sort()
  }, [checkInDates])

  useEffect(() => {
    setCurrentYear(availableYears.length > 0 ? availableYears[0] : new Date().getFullYear())
  }, [availableYears])

  const { t } = useTranslation()
  const modalSearchRef = useRef(null)
  const modalSearchContentRef = useRef(null)
  const isContentOverHeight = modalSearchContentHeight > modalSearchHeight
  const STAY_DAY_OPTIONS = useMemo(() => stayDayOptions(t), [])
  const serviceTypes = JSON.parse(
    (document.querySelector('[data-service-types]') as HTMLElement)?.dataset?.serviceTypes || '[]',
  ) as string[]
  const checkInDateUrl = (document.querySelector('[data-check-in-date-url]') as HTMLElement)?.dataset?.checkInDateUrl || ''
  const { validationErrors, setValidationErrors, openModalFilter, setOpenModalFilter } = usePlanStore()

  useEffect(() => {
    setModalSearchHeight(modalSearchRef?.current?.clientHeight)
    setModalSearchContentHeight(modalSearchContentRef?.current?.clientHeight)
    const checkInDatesData = (document.querySelector('[data-check-in-dates]') as HTMLElement)?.dataset?.checkInDates || '[]'
    setCheckInDates(JSON.parse(checkInDatesData))
  }, [])

  useEffect(() => {
    const params = {
      ...dataSearch,
      start_date: '',
      preschooler_birthdays: dataSearch.preschooler_birthdays
        .filter((birthday) => birthday.year && birthday.month && birthday.day)
        .map((birthday) => `${birthday.year}-${birthday.month}-${birthday.day}`)
        .join(','),
    }
    axios
      .get(checkInDateUrl, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        params,
      })
      .then((response) => {
        if (!response.data) return

        const checkInDates = response.data.check_in_dates as DataType[]
        if (checkInDates.length === 0 || checkInDates.some((checkInDate) => checkInDate.date === dataSearch.start_date)) {
          setDataSearch((prevDataSearch) => ({ ...prevDataSearch, start_date: '' }))
        }

        setCheckInDates(checkInDates)
      })
  }, [
    dataSearch.number_of_adults,
    dataSearch.number_of_preschoolers,
    dataSearch.number_of_students,
    dataSearch.number_of_preschoolers_1,
    dataSearch.number_of_students_1,
    dataSearch.stay_day,
    JSON.stringify(dataSearch.preschooler_birthdays),
  ])

  const handleChangeDataSearch = (
    key: KeyDataSearch,
    value: number | string | PreschoolerBirthdays,
    type?: 'down' | 'up' | 'option' | 'text',
  ) => {
    let newValue: number | string | PreschoolerBirthdays = value

    if (key === 'start_date' && value instanceof Date && value.getFullYear() !== currentYear) {
      setCurrentYear(value.getMonth() < 3 ? value.getFullYear() - 1 : value.getFullYear())
    }

    switch (type) {
      case 'up':
      case 'down': {
        let increment = type === 'up' ? 1 : -1
        if (type == 'down') {
          if (key === 'number_of_adults' && Number(dataSearch[key]) <= 1) {
            increment = 0
          }

          if (
            ['number_of_preschoolers', 'number_of_students'].includes(key) &&
            Number(dataSearch.number_of_preschoolers) + Number(dataSearch.number_of_students) <= 1
          ) {
            increment = 0
          }
        }

        let potentialNewValue = Number(dataSearch[key]) + increment
        newValue = Math.max(0, Math.min(20, potentialNewValue))
        const updateDataSearch = { ...dataSearch }
        if (key === 'number_of_preschoolers') {
          const updatePreschoolerBirthdays = [...dataSearch.preschooler_birthdays]
          if (type === 'up') {
            updatePreschoolerBirthdays.push({
              year: '',
              month: '',
              day: '',
            })
          } else {
            if (Number(dataSearch.number_of_preschoolers) > 1) {
              updatePreschoolerBirthdays.pop()
            }
          }
          updateDataSearch.preschooler_birthdays = updatePreschoolerBirthdays
        }
        setDataSearch({ ...updateDataSearch })
        break
      }
      case 'option': {
        if (key === 'stay_day') {
          setDataSearch((prevDataSearch) => ({ ...prevDataSearch, stay_day: value as string }))
          return
        }
        break
      }
      case 'text': {
        const isValueChosen = dataSearch[key] === value
        newValue = isValueChosen ? '' : value
        break
      }
    }

    setDataSearch((prevDataSearch) => ({ ...prevDataSearch, [key]: newValue }))
  }
  const handleClearForm = () => {
    const resetDataSearch = {
      ...DATA_SEARCH_INIT,
      preschooler_birthdays: [
        {
          year: '',
          month: '',
          day: '',
        },
      ],
    }
    setClickedReset(true)
    setDataSearch({ ...resetDataSearch })
    setValidationErrors({
      childBirthdate: false,
    })
  }

  const handleValidateForm = (value: DataSearchType) => {
    const validationResult = {
      isValid: true,
      errors: {
        childBirthdate: false,
      },
    }

    if (serviceTypes.some((serviceType) => serviceType.includes('保育園留学')) && value.number_of_preschoolers > 0) {
      if (value.number_of_preschoolers > 0 && value.preschooler_birthdays.length === value.number_of_preschoolers) {
        const isAllChildBirthdaysFilled = value.preschooler_birthdays.every((info) => info.year && info.month && info.day)
        if (!isAllChildBirthdaysFilled) {
          validationResult.isValid = true
          validationResult.errors.childBirthdate = false
        }
      }
      if (!value.preschooler_birthdays || value.preschooler_birthdays.length < value.number_of_preschoolers) {
        validationResult.isValid = false
        validationResult.errors.childBirthdate = true
      } else {
        const isAllChildBirthdaysFilled = value.preschooler_birthdays.every((info) => info.year && info.month && info.day)
        if (!isAllChildBirthdaysFilled) {
          validationResult.isValid = false
          validationResult.errors.childBirthdate = true
        }
      }
    }

    return validationResult
  }

  const handleSubmit = () => {
    const validationResult = handleValidateForm(dataSearch)
    if (!validationResult.isValid) {
      setValidationErrors(validationResult.errors)
      return
    } else {
      setValidationErrors({
        childBirthdate: false,
      })
    }

    if (clickedReset) {
      setClickedReset(false)
      window.location.href = window.location.pathname
      return
    }
    onChangeDataSearch(dataSearch, checkInDates)
  }

  const ChildTypeCounter = ({ label, dataKey = 'maximum_number_of_children' }) => {
    return (
      <div className="w-full grid grid-cols-12 mt-3 mb-3">
        <div className={`${isSmallMobileScreen ? 'col-start-1 col-span-6' : 'col-start-1 col-span-8'} text-left`}>
          <div>{label}</div>
        </div>
        <div
          className={`${
            isSmallMobileScreen ? 'col-start-7 col-span-6' : 'col-start-9 col-span-4'
          } flex items-center gap-2 h-8 ml-auto`}
        >
          <button
            onClick={() => handleChangeDataSearch(dataKey as KeyDataSearch, '', 'down')}
            className="div rounded-full cursor-pointer select-none"
          >
            <IconMinus />
          </button>
          <div className="mt-1 w-6 text-center">{dataSearch?.[dataKey]}</div>
          <button onClick={() => handleChangeDataSearch(dataKey as KeyDataSearch, '', 'up')} className="all-unset cursor-pointer">
            <IconPlus />
          </button>
        </div>
      </div>
    )
  }

  const handleCloseModalFilter = () => {
    handleClearForm()
    setOpenModalFilter(false)
  }

  if (!openModalFilter) return
  const renderChildrenTypeControl = () => {
    const handleOnChangeInDateSelectionField = (value: PreschoolerBirthdays, index: number) => {
      const updateDataSearch = { ...dataSearch }
      updateDataSearch.preschooler_birthdays[index] = value
      setDataSearch({ ...updateDataSearch })
    }
    return (
      <>
        {serviceTypes.some((serviceType) => serviceType.includes('保育園留学')) && (
          <>
            <ChildTypeCounter label={t('plan_detail.reservation.modal.people.nursery_aboard')} dataKey="number_of_preschoolers" />
            {[...Array(dataSearch.number_of_preschoolers || 0)].map((_, index) => (
              <div key={`preschooler-${index}`} className="mb-2">
                <div className="text-sm">
                  {t('plan_detail.reservation.modal.people.first_child_birthdate', { count: index + 1 })}
                  <span className="text-red-500">*</span>
                </div>
                <DateSelectionField
                  name={`fronts_reservation[children_birth_day][${index}]`}
                  hasError={validationErrors.childBirthdate}
                  value={dataSearch.preschooler_birthdays?.[index] || { year: '', month: '', day: '' }}
                  onChange={(value) => handleOnChangeInDateSelectionField(value, index)}
                />
              </div>
            ))}
          </>
        )}
        {serviceTypes.some((serviceType) => serviceType.includes('小学生留学')) && (
          <ChildTypeCounter label={t('plan_detail.reservation.modal.people.elementary_aboard')} dataKey="number_of_students" />
        )}
        <ChildTypeCounter label={t('plan_detail.reservation.modal.people.not_and_toddler')} dataKey="number_of_preschoolers_1" />
        <ChildTypeCounter label={t('plan_detail.reservation.modal.people.not_and_elementary')} dataKey="number_of_students_1" />
      </>
    )
  }

  return ReactDOM.createPortal(
    <div
      className="modal-search-backdrop fixed top-0 left-0 right-0 z-50 w-full overflow-x-hidden overflow-y-auto md:inset-0 max-h-full h-full justify-center items-center flex"
      role="dialog"
    >
      <div
        className="relative w-full overflow-y-auto remove-scroll-bar"
        style={{
          width: isMobileScreen ? '100%' : '420px',
          display: isMobileScreen ? 'block' : 'flex',
          alignItems: 'normal',
          height: isMobileScreen ? '100%' : isTabletScreen ? '77%' : '85%',
          animation: 'slide-down 0.4s ease-in-out forwards',
          borderRadius: isMobileScreen ? '0px' : '22px',
        }}
      >
        <div
          className="shadow dark:bg-gray-700"
          ref={modalSearchRef}
          style={{
            height: '100%',
            width: '100%',
            backgroundColor: isMobileScreen ? 'white' : 'transparent',
            borderRadius: isMobileScreen ? '0px' : '22px',
            marginLeft: isMobileScreen ? '0px' : 'auto',
            marginRight: isMobileScreen ? '0px' : 'auto',
          }}
        >
          <div
            className="flex bg-white items-center justify-between px-2 rounded-t"
            style={{
              borderRadius: isMobileScreen ? '0' : '16px 16px 0px 0px',
              height: isSmallMobileScreen ? '11%' : '7%',
            }}
          >
            <button
              className="text-gray-900 bg-transparent hover:bg-gray-200 rounded-lg text-sm ml-auto inline-flex items-center dark:hover:bg-gray-600 dark:hover:text-white p-1.5"
              data-modal-hide="number-personal-modal"
              type="button"
              onClick={handleCloseModalFilter}
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path
                  clipRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  fillRule="evenodd"
                />
              </svg>
            </button>
          </div>
          <div
            ref={modalSearchContentRef}
            className="md:mb-0 bg-white"
            style={{
              height: '80%',
              overflow: 'auto',
              padding: isSmallMobileScreen ? '10px' : '0rem 1rem 0.5rem',
            }}
          >
            <h3 className="mb-3 font-semibold dark:text-white text-black font-size-18">
              {t('plan_detail.reservation.modal.people.title')}
            </h3>
            {validationErrors.childBirthdate && (
              <div
                id="birthdate-notice"
                style={{
                  color: '#007CFF',
                  border: `1px solid #E5E7EB`,
                  borderRadius: '8px',
                  fontSize: '12px',
                  padding: '10px',
                  margin: '15px 0px',
                  backgroundColor: '#EFF6FE',
                }}
              >
                {t('plan_detail.reservation.modal.people.birthdate_notice')}
              </div>
            )}
            <div className="w-full grid grid-cols-12 mb-2">
              <div className={`${isSmallMobileScreen ? 'col-start-1 col-span-6' : 'col-start-1 col-span-8'} text-left`}>
                <div className="title font-bold">{t('plan_detail.reservation.modal.people.adult')}</div>
              </div>
              <div
                className={`${
                  isSmallMobileScreen ? 'col-start-7 col-span-6' : 'col-start-9 col-span-4'
                } flex items-cennter gap-2 h-8 ml-auto`}
              >
                <button
                  onClick={() => handleChangeDataSearch('number_of_adults', '', 'down')}
                  className="all-unset div rounded-full cursor-pointer select-none rounded-bl-full"
                >
                  <IconMinus />
                </button>
                <div className="text-center mt-1 w-6">{dataSearch.number_of_adults}</div>
                <button
                  className="all-unset rounded-full cursor-pointer rounded-bl-full md:ml-0"
                  onClick={() => handleChangeDataSearch('number_of_adults', '', 'up')}
                >
                  <IconPlus />
                </button>
              </div>
            </div>
            <div className="title font-bold">{t('plan_detail.reservation.modal.people.child')}</div>
            {renderChildrenTypeControl()}
            <h3 className="mt-4 font-semibold dark:text-white text-black font-size-18">{t('top.modal_search.stay_day.title')}</h3>
            <div className="mt-2 flex items-center justify-start gap-2">
              <span className="text-sm">{t('top.modal_search.stay_period')}</span>
              <select
                className="rounded-lg text-sm children-option border-gray-300 text-ellipsis"
                style={{
                  width: isSmallMobileScreen ? '95px' : '110px',
                }}
                value={dataSearch.stay_day}
                onChange={(e) => handleChangeDataSearch('stay_day', e.target.value, 'option')}
              >
                {STAY_DAY_OPTIONS.map((stayDay) => (
                  <option key={stayDay.id} value={stayDay.value} className="text-sm">
                    {stayDay.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="mt-4">
              <YearPagination currentYear={currentYear} availableYears={availableYears} setCurrentYear={setCurrentYear} />
            </div>
            <div className="mt-2">
              <DatePicker
                onChangeDataSearch={handleChangeDataSearch}
                initDate={dataSearch.start_date ? new Date(dataSearch.start_date) : undefined}
                full
                range={Number(dataSearch.stay_day)}
                data={checkInDates}
                startMonth={currentYear ? new Date(currentYear, 3, 1) : undefined}
                endMonth={currentYear ? new Date(currentYear + 1, 2, 1) : undefined}
              />
            </div>
            <YearPagination currentYear={currentYear} availableYears={availableYears} setCurrentYear={setCurrentYear} />
          </div>
          <div
            className="flex w-full items-center p-4 space-x-2 rounded-b bg-white"
            style={{
              borderRadius: isMobileScreen ? '0px' : '0px 0px 16px 16px',
              position: isMobileScreen || isContentOverHeight ? 'fixed' : 'unset',
              height: isMobileScreen || isTabletScreen ? '12%' : '10%',
            }}
          >
            <button
              className="w-1/2 md:w-1/2 text-gray-500 bg-white hover:bg-gray-100 focus:ring-4 focus:outline-none focus:ring-blue-300 rounded-lg border border-gray-200 text-sm font-medium px-5 hover:text-gray-900 focus:z-10 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-500 dark:hover:text-white dark:hover:bg-gray-600 dark:focus:ring-gray-600 py-2.5"
              style={{
                padding: isSmallMobileScreen ? '0.4rem 1rem' : '0.7rem 1rem',
              }}
              onClick={handleClearForm}
              type="button"
            >
              {t('top.filtering.button.clear')}
            </button>

            <button
              className="w-1/2 md:w-1/2 background-green-primary text-white focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800 py-2.5"
              onClick={handleSubmit}
              style={{
                padding: isSmallMobileScreen ? '0.4rem 1rem' : '0.7rem 1rem',
              }}
              type="button"
            >
              {t('top.filtering.button.apply')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}
